import React, { useMemo, useState, useEffect } from 'react';
import { Table, Card, Button, Segmented, Tooltip, Typography, Badge, Tag } from 'antd';
import { useLoanSchedule } from "@/hooks/loans/useLoans";
import moment from "moment";
import { ExpandAltOutlined, ShrinkOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {AppapproDTO} from "@/shared/api/model";

const { Text } = Typography;

interface LoanScheduleComponentProps {
    appapproDTO: AppapproDTO | null;
    /** Decimal places for installment rounding (0 = whole number, 2 = 2dp). Defaults to 2. */
    installmentRoundingPrecision?: number | null;
}

const LoanScheduleComponent = ({ appapproDTO, installmentRoundingPrecision }: LoanScheduleComponentProps) => {
    const [{ data: scheduleData, isLoading, error }] = useLoanSchedule(appapproDTO);
    const [expandedView, setExpandedView] = useState(false);
    const [pageSize, setPageSize] = useState(5);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen size
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Resolve precision: prop > default 2
    const precision = installmentRoundingPrecision ?? 2;

    // Format a number to the configured decimal places as TZS currency
    const formatAmount = (amount: number | null | undefined) => {
        if (amount == null) return '-';
        return amount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'TZS',
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        });
    };

    // Detect whether any installment has fee data populated
    const hasFees = useMemo(() => {
        if (!scheduleData?.scheduleDTO) return false;
        return scheduleData.scheduleDTO.some(
            (s: any) =>
                (s.applicationFee && s.applicationFee !== 0) ||
                (s.processingFee  && s.processingFee  !== 0) ||
                (s.commissionFee  && s.commissionFee  !== 0) ||
                (s.commitmentFee  && s.commitmentFee  !== 0)
        );
    }, [scheduleData]);

    // Define the summary data - first, middle and last payments
    const summaryData = useMemo(() => {
        if (!scheduleData?.scheduleDTO || scheduleData.scheduleDTO.length === 0) return [];
        
        const allData = [...scheduleData.scheduleDTO];
        
        if (allData.length <= 3) return allData;
        
        // Get first payment
        const firstPayment = allData[0];
        
        // Get middle payment
        const middleIndex = Math.floor(allData.length / 2);
        const middlePayment = allData[middleIndex];
        
        // Get last payment
        const lastPayment = allData[allData.length - 1];
        
        return [firstPayment, middlePayment, lastPayment];
    }, [scheduleData]);

    // Mobile-friendly columns - hide running columns on mobile
    const columns = useMemo(() => {
        const baseColumns: any[] = [
            {
                title: 'Date',
                dataIndex: 'trxDate',
                key: 'trxDate',
                render: (date: string) => moment(date).format('DD MMM, YYYY'),
                sorter: (a: any, b: any) => moment(a.trxDate).unix() - moment(b.trxDate).unix(),
                width: isMobile ? 100 : undefined,
            },
            {
                title: 'Principal',
                dataIndex: 'principal',
                key: 'principal',
                align: 'right' as const,
                render: (amount: number) => (
                    <span className="text-xs sm:text-sm">
                        {isMobile ? `TZS ${(amount / 1000).toFixed(0)}k` : formatAmount(amount)}
                    </span>
                ),
            },
            {
                title: 'Interest',
                dataIndex: 'interest',
                key: 'interest',
                align: 'right' as const,
                render: (amount: number) => (
                    <span className="text-xs sm:text-sm">
                        {isMobile ? `TZS ${(amount / 1000).toFixed(precision)}k` : formatAmount(amount)}
                    </span>
                ),
            },
        ];

        // Add desktop-only columns
        if (!isMobile) {
            // Fee columns – only shown when fees are present on installments
            if (hasFees) {
                baseColumns.push(
                    {
                        title: 'App. Fee',
                        dataIndex: 'applicationFee',
                        key: 'applicationFee',
                        align: 'right' as const,
                        render: (amount: number) => amount ? formatAmount(amount) : '-',
                    },
                    {
                        title: 'Proc. Fee',
                        dataIndex: 'processingFee',
                        key: 'processingFee',
                        align: 'right' as const,
                        render: (amount: number) => amount ? formatAmount(amount) : '-',
                    },
                    {
                        title: 'Comm. Fee',
                        dataIndex: 'commissionFee',
                        key: 'commissionFee',
                        align: 'right' as const,
                        render: (amount: number) => amount ? formatAmount(amount) : '-',
                    },
                    {
                        title: 'Commit. Fee',
                        dataIndex: 'commitmentFee',
                        key: 'commitmentFee',
                        align: 'right' as const,
                        render: (amount: number) => amount ? formatAmount(amount) : '-',
                    },
                );
            }

            baseColumns.push(
                {
                    title: 'Total Due',
                    dataIndex: 'totalDue',
                    key: 'totalDue',
                    align: 'right' as const,
                    render: (amount: number) => (
                        <Text strong>{formatAmount(amount)}</Text>
                    ),
                },
                {
                    title: 'Running Principal',
                    dataIndex: 'runningPrinciple',
                    key: 'runningPrinciple',
                    align: 'right' as const,
                    render: (amount: number) => formatAmount(amount),
                },
                {
                    title: 'Running Interest',
                    dataIndex: 'runningInterest',
                    key: 'runningInterest',
                    align: 'right' as const,
                    render: (amount: number) => formatAmount(amount),
                }
            );
        }

        return baseColumns;
    }, [isMobile, hasFees, precision, formatAmount]);

    const totals = useMemo(() => {
        if (!scheduleData?.scheduleDTO) return null;

        return (scheduleData.scheduleDTO as any[]).reduce((acc, curr) => ({
            principal:      (acc.principal      || 0) + (curr.principal      || 0),
            interest:       (acc.interest       || 0) + (curr.interest       || 0),
            totalDue:       (acc.totalDue       || 0) + (curr.totalDue       || 0),
            applicationFee: (acc.applicationFee || 0) + (curr.applicationFee || 0),
            processingFee:  (acc.processingFee  || 0) + (curr.processingFee  || 0),
            commissionFee:  (acc.commissionFee  || 0) + (curr.commissionFee  || 0),
            commitmentFee:  (acc.commitmentFee  || 0) + (curr.commitmentFee  || 0),
        }), { principal: 0, interest: 0, totalDue: 0, applicationFee: 0, processingFee: 0, commissionFee: 0, commitmentFee: 0 });
    }, [scheduleData]);

    if (error) {
        return (
            <div className="text-red-600 p-4">
                Error loading loan schedule: {error.message}
            </div>
        );
    }

    const formatDateDisplay = (date: string|undefined) => {
        if(!date) return "";
        return moment(date).format('DD MMMM, YYYY');
    };

    const calculateTermLength = () => {
        if (!scheduleData) return '';
        const start = moment(scheduleData.disbursementDate);
        const end = moment(scheduleData.expireDate);
        const months = end.diff(start, 'months');
        const years = Math.floor(months / 12);
        const remainingMonths = months % 12;

        let termText = '';
        if (years > 0) {
            termText += `${years} year${years > 1 ? 's' : ''}`;
            if (remainingMonths > 0) termText += ' and ';
        }
        if (remainingMonths > 0 || years === 0) {
            termText += `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
        }
        return termText;
    };

    const handleViewToggle = () => {
        setExpandedView(!expandedView);
    };

    // Calculate the number of installments
    const installmentCount = scheduleData?.scheduleDTO?.length || 0;

    return (
        <div className="space-y-4">
            {scheduleData && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <Card className="bg-blue-50" bodyStyle={{ padding: '12px' }}>
                            <div className="text-sm space-y-2">
                                <div>
                                    <span className="font-semibold">Disbursement Date:</span>{' '}
                                    {formatDateDisplay(scheduleData?.disbursementDate)}
                                </div>
                                <div>
                                    <span className="font-semibold">Expiry Date:</span>{' '}
                                    {formatDateDisplay(scheduleData?.expireDate)}
                                </div>
                                <div>
                                    <span className="font-semibold">Term Length:</span>{' '}
                                    {calculateTermLength()}
                                </div>
                                <div>
                                    <span className="font-semibold">Total Interest:</span>{' '}
                                    {scheduleData.totalInterest?.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'TZS',
                                    })}
                                </div>
                                <div>
                                    <span className="font-semibold">Number of Installments:</span>{' '}
                                    <Badge count={installmentCount} color="blue" />
                                </div>
                            </div>
                        </Card>
                        {totals && (
                            <Card className="bg-green-50" bodyStyle={{ padding: '12px' }}>
                                <div className="text-sm space-y-2">
                                    <div>
                                        <span className="font-semibold">Total Principal:</span>{' '}
                                        {formatAmount(totals.principal)}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Total Interest:</span>{' '}
                                        {formatAmount(totals.interest)}
                                    </div>
                                    {hasFees && (
                                        <>
                                            {totals.applicationFee > 0 && (
                                                <div>
                                                    <span className="font-semibold">Total Application Fee:</span>{' '}
                                                    {formatAmount(totals.applicationFee)}
                                                </div>
                                            )}
                                            {totals.processingFee > 0 && (
                                                <div>
                                                    <span className="font-semibold">Total Processing Fee:</span>{' '}
                                                    {formatAmount(totals.processingFee)}
                                                </div>
                                            )}
                                            {totals.commissionFee > 0 && (
                                                <div>
                                                    <span className="font-semibold">Total Commission Fee:</span>{' '}
                                                    {formatAmount(totals.commissionFee)}
                                                </div>
                                            )}
                                            {totals.commitmentFee > 0 && (
                                                <div>
                                                    <span className="font-semibold">Total Commitment Fee:</span>{' '}
                                                    {formatAmount(totals.commitmentFee)}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div className="pt-1 border-t border-green-200">
                                        <span className="font-semibold">Total Amount Due:</span>{' '}
                                        <Text strong>{formatAmount(totals.totalDue)}</Text>
                                        {hasFees && (
                                            <Tag color="blue" className="ml-2" style={{ fontSize: '10px' }}>incl. fees</Tag>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                        <div className="flex items-center justify-center w-full sm:w-auto">
                            <Text strong className="text-sm sm:text-base">
                                {expandedView ? 'Full Schedule' : 'Summary View'}
                            </Text>
                            {!expandedView && (
                                <Tooltip title="Showing first, middle, and last installments">
                                    <InfoCircleOutlined className="ml-2 text-blue-500" />
                                </Tooltip>
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
                            {expandedView && !isMobile && (
                                <Segmented
                                    options={[
                                        { label: '5 rows', value: 5 },
                                        { label: '10 rows', value: 10 },
                                        { label: '20 rows', value: 20 },
                                        { label: 'All', value: 0 },
                                    ]}
                                    value={pageSize}
                                    onChange={(value) => setPageSize(Number(value))}
                                    className="mr-0 sm:mr-4"
                                    size="small"
                                />
                            )}
                            <Button 
                                type="primary" 
                                icon={expandedView ? <ShrinkOutlined /> : <ExpandAltOutlined />} 
                                onClick={handleViewToggle}
                                size={isMobile ? 'middle' : 'middle'}
                                className="w-full sm:w-auto"
                            >
                                {expandedView ? 'Show Summary' : 'Show Full Schedule'}
                            </Button>
                        </div>
                    </div>
                </>
            )}

            <Table
                loading={isLoading}
                dataSource={expandedView ? scheduleData?.scheduleDTO : summaryData}
                columns={columns}
                pagination={expandedView ? (pageSize > 0 ? { pageSize, size: 'small' } : false) : false}
                rowKey="trxDate"
                className="w-full"
                size={isMobile ? 'small' : 'middle'}
                scroll={{ x: 'max-content' }}
                rowClassName={(record, index) =>
                    index % 2 === 0 ? 'bg-white' : 'bg-blue-50'
                }
                summary={() => {
                    if (!totals) return null;
                    const feeColCount = hasFees ? 4 : 0;
                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row className="bg-gray-100 font-semibold">
                                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    {formatAmount(totals.principal)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2} align="right">
                                    {formatAmount(totals.interest)}
                                </Table.Summary.Cell>
                                {hasFees && (
                                    <>
                                        <Table.Summary.Cell index={3} align="right">{formatAmount(totals.applicationFee)}</Table.Summary.Cell>
                                        <Table.Summary.Cell index={4} align="right">{formatAmount(totals.processingFee)}</Table.Summary.Cell>
                                        <Table.Summary.Cell index={5} align="right">{formatAmount(totals.commissionFee)}</Table.Summary.Cell>
                                        <Table.Summary.Cell index={6} align="right">{formatAmount(totals.commitmentFee)}</Table.Summary.Cell>
                                    </>
                                )}
                                <Table.Summary.Cell index={3 + feeColCount} align="right">
                                    <Text strong>{formatAmount(totals.totalDue)}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={4 + feeColCount} align="right">-</Table.Summary.Cell>
                                <Table.Summary.Cell index={5 + feeColCount} align="right">-</Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
            
            {!expandedView && scheduleData?.scheduleDTO && scheduleData.scheduleDTO.length > 3 && (
                <div className="text-center mt-2 text-gray-500 text-sm">
                    <Text type="secondary">
                        Showing 3 of {scheduleData.scheduleDTO.length} installments. 
                        <Button type="link" onClick={handleViewToggle} size="small">
                            Show all installments
                        </Button>
                    </Text>
                </div>
            )}
        </div>
    );
};

export default LoanScheduleComponent;